/**
 * Tanjong Life · "Commission an SIU triage sentinel that watches the referral queue
 * between the runs."
 *
 * The second born agent, and the one the fraud act needs: workflow 02 ends when a claim
 * is ranked in front of an officer, and nothing has ever watched what happens to it
 * afterwards. Commissioned from the same nine moves as the integrity sentinel
 * (`commission-scenario.ts`), with its own read of the org, its own three probes and the
 * fourth it refuses to run.
 *
 * Two things the room should see land with the commission and not before: the Financial
 * Crime desk gains a member (it holds two agents until this sentence is typed), and the
 * requirement-set base gains a reader. Both come from `patches`, not from the seed.
 *
 * Registered before the five builds: this sentence contains `siu triage`, which
 * `tanjong-build-fraud` also owns.
 */
import type { Scenario } from '../director.js';
import {
  CLAIMS_AUTHORITIES,
  type CommissionAsset,
  makeCommission,
  once,
} from './commission-scenario.js';
import { MAKE_SIU_SENTINEL } from './tanjong-make-intents.js';

const load = once(() =>
  import('../assets/tanjong-siu-triage-sentinel.json').then(
    (m) => m.default as unknown as CommissionAsset,
  ),
);

export const tanjongCommissionSiu: Scenario = makeCommission({
  id: 'tanjong-commission-siu',
  keywords: [
    'commission an siu',
    'siu triage sentinel',
    'watches the referral queue',
    'watch the referral queue',
  ],
  intents: [MAKE_SIU_SENTINEL],
  pk: 'siu-triage-sentinel',
  name: 'SIU Triage Sentinel',
  load,
  thinking: [
    {
      thought:
        'Workflow 02 ends the moment a claim is ranked in front of an officer. Everything after that — whether anybody opened it, what they wrote when they cleared it, whether the weight it was ranked under has since moved — is outside the run. That is the gap, and it is the whole job.',
    },
    {
      tool: {
        name: 'processes.read',
        input: 'org=tanjong-life · tl-pr12 · gates, queue writes and dispositions',
        output:
          'Rank is written to the SIU queue under the process identity · the disposition gate is a person · no step reads what happens to a rank after it lands',
      },
    },
    {
      tool: {
        name: 'controls.read',
        input: 'control=SIU referral threshold · version history',
        output:
          'v4 at 70, owned by the Financial Crime desk · the indicator register carries a re-issue that moved the panel-tenure weight',
      },
    },
    {
      thought:
        'The Claims Fraud Analyst scores and ranks, and it is deliberately not autonomous — a scorer that could also refer or clear would be marking its own work. So this watches the queue, and it must not be allowed to touch the queue either.',
    },
  ],
  watch: {
    text: 'I will commission it as a watch inside the referral queue — not over any claim. Three things to start with. Claims ranked above the referral threshold that no officer has ever opened, aged from the rank rather than from intake. Clearances recorded with the reason field left empty, named but never filled in. And queue positions built against an indicator weight that moved before the officer got to them, reported with which of them would sit in a different decile today. There is a fourth question it will refuse to answer, and I would rather you saw it refuse than never hear it asked.',
    sources: [
      'tl-pr12 · disposition gate',
      'SIU referral threshold · v4 · Financial Crime desk',
      'Indicator register · panel-tenure re-issue',
    ],
  },
  offer:
    'It opens a case at a named officer with the rank, the threshold version and the score explanation attached. It refers nothing, clears nothing, re-ranks nothing and re-scores nothing. Before I deploy it, here are the authorities it COULD be given. I have selected none of them.',
  authorities: CLAIMS_AUTHORITIES,
  refusal:
    'It takes none. All five are wired on the agent and switched off — 0 of 0, and the action on breach is Halt. That matters more here than anywhere else in this act: the one thing an agent watching a fraud queue must never be able to do is change the fraud queue. If the desk ever wants one of them on, that is a versioned change to the authority matrix a named person signs, not a setting.',
  appsPrompt:
    'And here is what it may READ. Ten systems, every one ticked from your org graph and every one read-only. Untick any you would rather it never opened.',
  apps: [
    { id: 'claimscore', label: 'Claims administration · FINEOS Claims' },
    { id: 'providernet', label: 'Panel & provider network' },
    { id: 'siudesk', label: 'Special investigations case desk' },
    { id: 'policyledger', label: 'Policy & benefits · via Pega Customer Service' },
    { id: 'm365', label: 'Mail & fax intake · Microsoft 365' },
    { id: 'modelgateway', label: 'Model gateway · private inference' },
    { id: 'disbursements', label: 'Disbursements · Oracle Fusion Financials' },
    { id: 'whatsapp', label: 'Policyholder messaging · WhatsApp Business' },
    { id: 'coverrules', label: 'Underwriting rules · ALLFINANZ / Magnum' },
    { id: 'pegacs', label: 'Servicing 360 · Pega Customer Service' },
    { id: 'slack', label: 'Desk messaging · Slack — answer only, it opens nothing' },
  ],
  group: {
    pk: 'financial-crime',
    member: {
      pk: 'siu-triage-sentinel',
      name: '@SIU Triage Sentinel',
      role: 'Watches the queue after the rank; refers and clears nothing',
      icon: 'radar',
      color: 'var(--red)',
      bg: 'var(--red-tint)',
    },
  },
  kb: { id: 'kb-claims-requirements', scope: 'read · FRAUD-IND-SET' },
  summary:
    'SIU Triage Sentinel commissioned · five authorities, every one 0 of 0 · Halt · read-only on the systems left ticked',
  commissioned:
    'Commissioned. The Financial Crime desk has a third member as of a moment ago, it can read the requirement sets, and it is sweeping the queue now — open it and watch it build its three probes, then hold the fourth and write the reason into the probe itself.',
  linkLabel: 'Open SIU Triage Sentinel — watch it start',
});
